import { useState, useEffect } from 'react';
import { fetchApi } from '../../lib/api';

export default function InventorNDAPanel({ connectionId, companyName, companyNdaUrl, companyNdaLink, slot, onRefresh }) {
  const [ndaRecords, setNdaRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signatureText, setSignatureText] = useState('');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!connectionId) { setLoading(false); return; }
    const load = async () => {
      try {
        const res = await fetchApi(`/api/nda/${connectionId}`);
        setNdaRecords(res.records || []);
      } catch { setNdaRecords([]); }
      setLoading(false);
    };
    load();
  }, [connectionId]);

  if (loading) return <p className="text-xs text-gray-400 py-2">Loading NDA status...</p>;

  // Determine status
  const latest = ndaRecords?.[0];
  let status = 'unsigned';
  if (latest?.company_action_at) status = 'fully_executed';
  else if (latest?.inventor_signed_at) status = 'awaiting_company';

  const isAwaitingCountersign = latest && latest.inventor_signed_at && !latest.company_action_at;
  const isNdaComplete = latest && !!latest.company_action_at;

  // Fully executed — show Zoom link (gated on NDA completion)
  if (status === 'fully_executed') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-emerald-600">✓</span>
          <span className="text-sm font-semibold text-emerald-700">NDA Complete</span>
          {isNdaComplete && (
            <span style={{
              background: '#ECFDF5', color: '#10B981', borderRadius: 9999,
              padding: '4px 8px', fontSize: 11, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
              display: 'inline-block',
            }}>NDA Complete</span>
          )}
        </div>
        {slot?.zoom_link ? (
          isNdaComplete ? (
            <a
              href={slot.zoom_link}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-1 px-4 py-2 rounded-btn text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              Join Meeting
            </a>
          ) : (
            <span style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
              Zoom link unlocks after NDA is complete
            </span>
          )
        ) : null}
      </div>
    );
  }

  // Awaiting company countersign
  if (status === 'awaiting_company') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-amber-600">✓</span>
          <span className="text-sm font-semibold text-amber-700">You've signed the NDA</span>
          {isAwaitingCountersign && (
            <span style={{
              background: '#FFFBEB', color: '#F59E0B', borderRadius: 9999,
              padding: '4px 8px', fontSize: 11, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
              display: 'inline-block',
            }}>Awaiting countersign</span>
          )}
        </div>
        <p className="text-xs text-amber-600 leading-relaxed">
          Waiting for {companyName} to countersign. You'll receive your Zoom link by email once complete.
        </p>
        {slot?.zoom_link && (
          <span style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', display: 'block', marginTop: 8 }}>
            Zoom link unlocks after NDA is complete
          </span>
        )}
      </div>
    );
  }

  // Unsigned — show signing form
  const docUrl = companyNdaUrl || companyNdaLink;

  const handleSign = async () => {
    if (!signatureText.trim()) { setError('Please type your full legal name'); return; }
    setSigning(true);
    setError('');
    try {
      await fetchApi('/api/nda/sign', {
        method: 'POST',
        body: JSON.stringify({
          connection_id: connectionId,
          signature_text: signatureText,
          document_url: docUrl || 'standard',
          nda_version: 1,
        }),
      });
      // Refresh parent + reload NDA
      const res = await fetchApi(`/api/nda/${connectionId}`);
      setNdaRecords(res.records || []);
      setSignatureText('');
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err.message);
    }
    setSigning(false);
  };

  return (
    <div className="border-2 border-vault-gold/20 rounded-xl p-4 mt-3 bg-amber-50/30">
      <h4 className="text-sm font-semibold text-vault-navy mb-2">Sign NDA with {companyName}</h4>

      {docUrl ? (
        <a href={docUrl} target="_blank" rel="noreferrer" className="inline-block text-xs text-vault-gold font-semibold hover:underline mb-3">
          View NDA Document →
        </a>
      ) : (
        <p className="text-xs text-gray-400 mb-3 leading-relaxed">
          This company uses a standard NDA. By signing below you agree to keep all discussed ideas confidential.
        </p>
      )}

      <p className="text-[11px] text-gray-400 leading-relaxed mb-3">
        By typing your full legal name below, you are electronically signing this Non-Disclosure Agreement.
        This signature is legally binding under the E-SIGN Act.
      </p>

      {error && <div className="text-xs text-vault-error bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3">{error}</div>}

      <input
        className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white focus:border-vault-gold focus:outline-none mb-3"
        placeholder="Type your full legal name"
        value={signatureText}
        onChange={e => setSignatureText(e.target.value)}
      />
      <button
        onClick={handleSign}
        disabled={signing}
        className="w-full py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
      >
        {signing ? 'Signing...' : 'Sign NDA'}
      </button>
    </div>
  );
}
